import React, { useCallback, useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
} from '@mesh.ai/rabbit';
import { useSelector } from 'react-redux';
import { INSIGHT_VIEWS } from 'reducers/okrsPageSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { okrsInsightsSelector } from 'selectors/okrsInsightsSelector';

import { CardView, ChevronDown, FlatList, TreeView } from 'assets/assetsV1/iconComponents';
import { CascadedList } from 'assets/assetsV1/iconComponents/CascadedList';

import { OKRManageViewType, useOKRManageViewType } from '../../hooks/useOKRManageViewType';

const OKRManageViews = () => {
  const { viewType, setViewType } = useOKRManageViewType();
  const { id: selectedCohortId } = useSelectedView();

  const { viewType: groupByType } = useSelector(okrsInsightsSelector);

  const isGroupByIndividual = groupByType === INSIGHT_VIEWS[0].value;

  // if it's my data or company
  const hideCardAndTreeView = [-12, -1].includes(selectedCohortId);

  const handleViewChange = useCallback(
    (view: OKRManageViewType) => {
      setViewType(view);
    },
    [setViewType]
  );

  const options = useMemo(() => {
    const dropdownOptions = [
      {
        label: 'List',
        value: 'list',
        onClick: () => handleViewChange('list'),
        Icon: FlatList,
      },
    ];

    if (!isGroupByIndividual) {
      dropdownOptions.push({
        label: 'List with hierarchy',
        value: 'listWithHierarchy',
        onClick: () => handleViewChange('listWithHierarchy'),
        Icon: CascadedList,
      });
    }

    if (!hideCardAndTreeView && !isGroupByIndividual) {
      dropdownOptions.push(
        {
          label: 'Card',
          value: 'card',
          onClick: () => handleViewChange('card'),
          Icon: CardView,
        },
        {
          label: 'Tree',
          value: 'tree',
          onClick: () => handleViewChange('tree'),
          Icon: TreeView,
        }
      );
    }

    return dropdownOptions;
  }, [handleViewChange, hideCardAndTreeView, isGroupByIndividual]);

  return (
    <Flex h="100%" w="100%" alignItems="center" justifyContent="center">
      <Dropdown>
        <DropdownTrigger asChild>
          <Button variant="text" className="group">
            <FlatList boxSize="24px" />
            <ChevronDown boxSize="24px" className="group-data-[state=open]:rotate-180" />
          </Button>
        </DropdownTrigger>
        <DropdownContent align="end">
          <DropdownRadioGroup value={viewType}>
            {options.map((option) => {
              const { label, onClick, value, Icon } = option;
              return (
                <DropdownRadioItem key={value} value={value} onSelect={onClick}>
                  <Flex alignItems="center">
                    <Icon mr="8px" color="currentColor" boxSize="24px" />
                    <span>{label}</span>
                  </Flex>
                </DropdownRadioItem>
              );
            })}
          </DropdownRadioGroup>
        </DropdownContent>
      </Dropdown>
    </Flex>
  );
};

export { OKRManageViews };
